// Project permission helpers, driven by the backend's effective-access payload
// (see src/api/access.ts). Replaces the earlier role-name string matching from
// FD2-917, which compared substrings and let 'Client / Admin' satisfy an
// includes('admin') check (FD2-1129).
//
// These gates decide what the UI *offers*. The API remains the actual gate.

import type { MyAccess, Permission, ProjectAccess } from '../api/access';

/**
 * Whether the access payload grants a permission, honouring wildcards.
 *
 * The backend's `can` map is not expanded — a holder of `documents:*` has no
 * `documents:create` key — so a plain lookup under-reports. This checks the
 * global `*`, the exact permission, and the owning module's `<module>:*`.
 *
 * @param access Effective access for the project, or null while unresolved.
 * @param permission Permission string to test, e.g. `members:manage`.
 */
export const hasPermission = (
  access: ProjectAccess | null | undefined,
  permission: Permission,
): boolean => {
  const granted = access?.permissions;
  if (!granted?.length) return false;

  if (granted.includes('*')) return true;
  if (granted.includes(permission)) return true;

  const moduleName = permission.split(':')[0];
  return !!moduleName && granted.includes(`${moduleName}:*`);
};

/** Whether the user may change a task's owner (Action Items + Project Plan). */
export const canEditTaskOwner = (access: ProjectAccess | null | undefined): boolean =>
  hasPermission(access, 'tasks:update');

/** Whether the user may edit project fields, including the scope of work. */
export const canEditProject = (access: ProjectAccess | null | undefined): boolean =>
  hasPermission(access, 'project:update');

/** Whether the user may add, edit, remove or re-role team members. */
export const canManageTeam = (access: ProjectAccess | null | undefined): boolean =>
  hasPermission(access, 'members:manage');

/**
 * Whether the user may delete the project.
 *
 * There is no `project:delete` permission in the catalog — only the `*` bundle
 * covers it — so project ownership stays an explicit second path. Without it,
 * an owner whose role is not seeded as PROJECT_OWNER would lose the action.
 *
 * @param isOwner Whether the current user is the project's owner_id.
 */
export const canDeleteProject = (
  access: ProjectAccess | null | undefined,
  isOwner?: boolean,
): boolean => !!isOwner || hasPermission(access, '*');

/**
 * Whether the user may change project settings.
 *
 * @param isOwner Whether the current user is the project's owner_id.
 */
export const canManageProjectSettings = (
  access: ProjectAccess | null | undefined,
  isOwner?: boolean,
): boolean => !!isOwner || hasPermission(access, 'project:update');

/**
 * Whether the user may view the project's document download log report.
 *
 * There is no dedicated `reports:*`/`documents:download_logs` permission in
 * the catalog, so this covers owner and system-admin the same way
 * `canDeleteProject` does: ownership stays an explicit boolean, and a
 * system-admin's resolved access always includes the `*` wildcard.
 *
 * @param isOwner Whether the current user is the project's owner_id.
 */
export const canViewDownloadLogs = (
  access: ProjectAccess | null | undefined,
  isOwner?: boolean,
): boolean => !!isOwner || hasPermission(access, '*');

/** Whether the user is a system-admin — a global role, not project-scoped. */
export const isSystemAdmin = (myAccess: MyAccess | null | undefined): boolean =>
  myAccess?.globalRole === 'system-admin';

/** Whether the user's global role can ever own a project — the only roles
 * that can create one (`POST /projects` is gated to these two) — and so
 * the only roles that can have anything under the archived list. */
export const canOwnProjects = (myAccess: MyAccess | null | undefined): boolean =>
  myAccess?.globalRole === 'system-admin' || myAccess?.globalRole === 'internal-user';

/**
 * Resolve the label to show for a project member's role.
 *
 * The access payload describes only the calling user, so the RBAC-derived
 * `projectRoleTitle` is used for their own row and the stored role name (with
 * Agency presented as Company) for everyone else.
 *
 * @param roleName Stored role name from the project roles list.
 * @param titleForCurrentUser The caller's projectRoleTitle, when this row is theirs.
 */
export const getDisplayRoleName = (
  roleName?: string | null,
  titleForCurrentUser?: string | null,
): string => {
  if (titleForCurrentUser) return titleForCurrentUser;
  if (!roleName) return 'Unassigned';
  return roleName.replace('Agency', 'Company');
};
